import React, { useState } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import cn from 'classnames';
import { Link, useLocation } from 'react-router-dom';
import styles from './Nav.module.scss';
import Image from '../../Image';
import logo from '../../../assets/logo.jpg';
import menu from '../../../assets/nav/menu.png';
import { routes } from '../../../routes';
import useQueryString from '../../../hooks/use-query-string';
import cart from '../../../assets/nav/cart.png';
import { useCustomer } from '../../../contexts/CustomerContext';
import useNumCartItems from '../../../hooks/useNumCartItems';

export default function Nav() {
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const { pathname } = useLocation();
  const q = useQueryString();
  const { customer } = useCustomer();

  const getCurrentDomain = () => {
    const hostname = window.location.hostname;
    if (hostname.includes('shoob.cloud')) {
      return 'https://www.shoob.cloud';
    } else {
      return 'https://www.shoobphoto.com';
    }
  }

  const { data } = useNumCartItems({ id: customer?.cart?.id, email: customer?.cart?.email || customer?.email });

  useDeepCompareEffect(() => {
    setIsLoginOpen(false);
  }, [pathname, q]);

  const isSignedIn = document.querySelector('#current-user');

  return (
    <nav
      className={cn({
        [styles.navContainer]: true,
      })}
      style={{ display: 'flex', justifyContent: 'space-between' }}
    >
      <div className={cn(styles.cartLink, styles.dropdown)} style={{ marginLeft: '1em' }}>
        <button
          className={styles.button}
          style={{ padding: '.5em' }}
          onClick={() => setIsLoginOpen(!isLoginOpen)}
          type="button"
        >
          <Image alt="Menu" src={menu} />
        </button>
        <div
          className={cn(styles.menuBackdrop, { [styles.menuBackdropVisible]: isLoginOpen })}
          onClick={() => setIsLoginOpen(false)}
          aria-hidden="true"
        />
        <ul
          className={cn({
            [styles.dropdownMenu]: true,
            [styles.hidden]: !isLoginOpen,
          })}
        >
          {/* Header: X close + brand wordmark */}
          <li className={styles.drawerTopBar}>
            <button
              className={styles.drawerCloseButton}
              onClick={() => setIsLoginOpen(false)}
              type="button"
              aria-label="Close menu"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <span className={styles.drawerBrand}>
              <Image alt="Shoob Photography" src={logo} />
            </span>
          </li>

          {/* ORDER PHOTOS */}
          <li className={styles.drawerSectionLabel}>Order photos</li>
          <li className={styles.drawerItem}>
            <Link to={routes.studentIdEntry()} onClick={() => setIsLoginOpen(false)}>
              <svg className={styles.drawerItemIcon} viewBox="0 0 24 24" aria-hidden="true">
                <path d="M3 7h4l2-2h6l2 2h4v12H3z" />
                <circle cx="12" cy="13" r="3.5" />
              </svg>
              School pictures
            </Link>
          </li>
          <li className={styles.drawerItem}>
            <Link to={routes.studentIdEntry({ order: 'senior' })} onClick={() => setIsLoginOpen(false)}>
              <svg className={styles.drawerItemIcon} viewBox="0 0 24 24" aria-hidden="true">
                <rect x="4" y="4" width="16" height="16" rx="2" />
                <circle cx="12" cy="11" r="3" />
                <path d="M7 19c1.5-2.5 8-2.5 10 0" />
              </svg>
              Senior portraits
            </Link>
          </li>
          <li className={styles.drawerItem}>
            <Link to={routes.studentIdEntry({ order: 'yearbook_order' })} onClick={() => setIsLoginOpen(false)}>
              <svg className={styles.drawerItemIcon} viewBox="0 0 24 24" aria-hidden="true">
                <path d="M5 4h11a3 3 0 013 3v13H8a3 3 0 01-3-3V4z" />
                <path d="M5 17a3 3 0 013-3h11" />
              </svg>
              Yearbooks
            </Link>
          </li>
          <li className={styles.drawerItem}>
            <Link to={routes.studentIdEntry({ order: 'panorama' })} onClick={() => setIsLoginOpen(false)}>
              <svg className={styles.drawerItemIcon} viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="8" cy="9" r="3" />
                <circle cx="16" cy="9" r="3" />
                <path d="M2 20c1-3 4-4 6-4s5 1 6 4M14 20c1-3 4-4 6-4" />
              </svg>
              Groups &amp; panorama
            </Link>
          </li>

          <li className={styles.drawerSectionGap} />

          {/* SENIOR PORTRAITS */}
          <li className={styles.drawerSectionLabel}>Senior portraits</li>
          <li className={styles.drawerItem}>
            <a href="/seniors" onClick={() => setIsLoginOpen(false)}>
              <svg className={styles.drawerItemIcon} viewBox="0 0 24 24" aria-hidden="true">
                <path d="M2 10l10-5 10 5-10 5z" />
                <path d="M6 12v5c2 2 10 2 12 0v-5" />
                <path d="M22 10v5" />
              </svg>
              About senior portraits
            </a>
          </li>
          <li className={styles.drawerItem}>
            <Link to={routes.scheduleAppointment({ senior: true, p: 601 })} onClick={() => setIsLoginOpen(false)}>
              <svg className={styles.drawerItemIcon} viewBox="0 0 24 24" aria-hidden="true">
                <rect x="3" y="5" width="18" height="16" rx="2" />
                <path d="M3 10h18M8 3v4M16 3v4" />
              </svg>
              Book a session
            </Link>
          </li>
          <li className={styles.drawerItem}>
            <Link to={routes.scheduleAppointment({ senior: true, p: 601 })} onClick={() => setIsLoginOpen(false)}>
              <svg className={styles.drawerItemIcon} viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 3l3 6 6 .8-4.5 4.2 1.2 6.5L12 17.5 6.3 20.5l1.2-6.5L3 9.8 9 9z" />
              </svg>
              Upgrade my session
            </Link>
          </li>
          <li className={styles.drawerItem}>
            <a href="/seniors#faq" onClick={() => setIsLoginOpen(false)}>
              <svg className={styles.drawerItemIcon} viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M9.5 9a2.5 2.5 0 015 0c0 1.5-2.5 2-2.5 4M12 17h.01" />
              </svg>
              Seniors FAQ
            </a>
          </li>

          <li className={styles.drawerSectionGap} />

          {/* BOOK APPOINTMENT */}
          <li className={styles.drawerSectionLabel}>Book appointment</li>
          <li className={styles.drawerItem}>
            <Link to={routes.scheduleAppointment({ senior: true, p: 601 })} onClick={() => setIsLoginOpen(false)}>
              <svg className={styles.drawerItemIcon} viewBox="0 0 24 24" aria-hidden="true">
                <path d="M2 10l10-5 10 5-10 5z" />
                <path d="M6 12v5c2 2 10 2 12 0v-5" />
              </svg>
              Senior portrait session
            </Link>
          </li>
          <li className={styles.drawerItem}>
            <Link to={routes.scheduleAppointment({ senior: false })} onClick={() => setIsLoginOpen(false)}>
              <svg className={styles.drawerItemIcon} viewBox="0 0 24 24" aria-hidden="true">
                <path d="M3 7h4l2-2h6l2 2h4v12H3z" />
                <circle cx="12" cy="13" r="3.5" />
              </svg>
              Elementary appointment
            </Link>
          </li>

          <li className={styles.drawerSectionGap} />

          {/* CUSTOMERS */}
          <li className={styles.drawerSectionLabel}>Customers</li>
          <li className={styles.drawerItem}>
            <Link to={routes.downloadImageForm()} onClick={() => setIsLoginOpen(false)}>
              <svg className={styles.drawerItemIcon} viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 3v12M7 11l5 5 5-5M4 21h16" />
              </svg>
              Download digital files
            </Link>
          </li>
          <li className={styles.drawerItem}>
            <Link to={routes.cart()} onClick={() => setIsLoginOpen(false)}>
              <svg className={styles.drawerItemIcon} viewBox="0 0 24 24" aria-hidden="true">
                <path d="M3 7h2l2 11h11l2-7H7" />
                <circle cx="9" cy="21" r="1.5" />
                <circle cx="17" cy="21" r="1.5" />
              </svg>
              My cart
            </Link>
          </li>
          <li className={styles.drawerItem}>
            <a href={getCurrentDomain() === 'https://www.shoob.cloud' ? 'https://www.shoobphotosouth.com/contact1' : '/contacts/new'}>
              <svg className={styles.drawerItemIcon} viewBox="0 0 24 24" aria-hidden="true">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.13.79.34 1.57.63 2.32a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.76-1.76a2 2 0 012.11-.45c.75.29 1.53.5 2.32.63A2 2 0 0122 16.92z" />
              </svg>
              Contact us
            </a>
          </li>
          <li className={styles.drawerItem}>
            <Link to={routes.registration()} onClick={() => setIsLoginOpen(false)}>
              <svg className={styles.drawerItemIcon} viewBox="0 0 24 24" aria-hidden="true">
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="M3 7l9 6 9-6" />
              </svg>
              Register for Picture Day Updates
            </Link>
          </li>

          <li className={styles.drawerSectionGap} />

          {/* FOR SCHOOLS */}
          <li className={styles.drawerSectionLabel}>For schools</li>
          <li className={styles.drawerItem}>
            <a href="/yearbooks" onClick={() => setIsLoginOpen(false)}>
              <svg className={styles.drawerItemIcon} viewBox="0 0 24 24" aria-hidden="true">
                <path d="M5 4h11a3 3 0 013 3v13H8a3 3 0 01-3-3V4z" />
                <path d="M5 17a3 3 0 013-3h11" />
              </svg>
              Yearbooks
            </a>
          </li>
          <li className={styles.drawerItem}>
            <a href="/yearbook_requests/new" onClick={() => setIsLoginOpen(false)}>
              <svg className={styles.drawerItemIcon} viewBox="0 0 24 24" aria-hidden="true">
                <rect x="5" y="4" width="14" height="17" rx="2" />
                <path d="M9 3h6v3H9zM8 11l3 3 5-5" />
              </svg>
              Yearbook sign-up
            </a>
          </li>
          <li className={styles.drawerItem}>
            <a href="/school_resources" onClick={() => setIsLoginOpen(false)}>
              <svg className={styles.drawerItemIcon} viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 21V10l8-5 8 5v11" />
                <rect x="9" y="14" width="6" height="7" />
                <path d="M12 5V3" />
              </svg>
              Resources for schools
            </a>
          </li>

          <li className={styles.drawerSectionGap} />

          {/* SCHOOL & EMPLOYEE LOGINS */}
          <li className={styles.drawerSectionLabel}>School &amp; Employee Login</li>
          <li className={cn(styles.drawerItem, styles.drawerItemMuted)}>
            <a href={isSignedIn ? '/pages/home' : '/users/sign_in'}>
              <svg className={styles.drawerItemIcon} viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 21V10l8-5 8 5v11" />
                <rect x="9" y="14" width="6" height="7" />
                <path d="M12 5V3" />
              </svg>
              {isSignedIn ? 'Home page' : 'School login'}
            </a>
          </li>
          {!isSignedIn && (
            <li className={cn(styles.drawerItem, styles.drawerItemMuted)}>
              <a href="/users/sign_in">
                <svg className={styles.drawerItemIcon} viewBox="0 0 24 24" aria-hidden="true">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M4 21v-2a8 8 0 0116 0v2" />
                </svg>
                Employee login
              </a>
            </li>
          )}

          {/* FOOTER */}
          <li className={styles.drawerFooterLinks}>
            <a href='/privacy-policy'>Privacy &amp; data policy</a>
          </li>
        </ul>
      </div>
      <Link to="/">
        <Image src={logo} classes={styles.logo} />
      </Link>
      <Link className={styles.cartLink} to={routes.cart()} style={{ paddingRight: '1em' }}>
        <div style={{ position: 'relative' }}>
          <Image src={cart} />
          {data > 0 && <span style={{ textAlign: 'center', position: 'absolute', top: '-8px', right: '-10px', background: '#008fcf', color: '#fff', minWidth: '18px', height: '18px', borderRadius: '9px', padding: '0 5px', fontSize: '11px', lineHeight: '18px', fontWeight: 600 }}>{data}</span>}
        </div>
      </Link>
    </nav>
  );
}
